<template>
    <div class="p-6">
        <PageTitleComponent title="Customer List" />
        <div v-if="!isModal" class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div class="bg-white p-4 shadow-lg rounded-lg flex items-center justify-between">
                <div>
                    <h3 class="text-lg font-semibold text-gray-600">Total Customers</h3>
                    <p class="text-2xl font-bold">{{ totalCustomerCount }}</p>
                </div>
                <LucideUsers class="w-8 h-8 text-blue-600" />
            </div>
            <div class="bg-white p-4 shadow-lg rounded-lg flex items-center justify-between">
                <div>
                    <h3 class="text-lg font-semibold text-gray-600">Active Customers</h3>
                    <p class="text-2xl font-bold">{{ totalCustomerCount }}</p>
                </div>
                <LucideCheckCircle class="w-8 h-8 text-green-600" />
            </div>
        </div>
        <div class="flex flex-wrap justify-between items-center mb-4 gap-4">
            <div class="flex items-center">
                <span class="mr-2">Show</span>
                <select v-model="pageLength" class="border px-4 py-2 rounded-lg w-20 text-center text-lg">
                    <option v-for="num in [5, 10, 15]" :key="num" :value="num">{{ num }}</option>
                </select>
                <span class="ml-2">entries</span>
            </div>
            <div class="flex gap-4">
                <span class="m-2">Group</span>
                <select v-model="customer_group" class="border px-4 py-2 rounded-lg">
                    <option value="">All</option>
                    <option value="vip">VIP</option>
                    <option value="regular">Regular</option>
                    <option value="wholesale">Wholesale</option>
                </select>
            </div>
            <input v-model="searchQuery" type="text" placeholder="Search chart of customers..."
                class="px-4 py-2 border rounded-lg flex-grow focus:ring-2 focus:ring-blue-500" />
            <router-link :to="{ name: 'NewCustomer', query: { redirect: $route.fullPath } }"
                class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2">
                <LucideUserPlus class="w-5 h-5" />
                New
            </router-link>
        </div>
        <PaginationComponent :currentPage="currentPage" :totalPages="totalPages" @page-change="handlePageChange" />
        <div class="overflow-x-auto bg-white shadow-lg rounded-xl">
            <table class="min-w-full text-sm text-center">
                <thead>
                    <tr class="bg-theme-2 border-b text-white">
                        <th class="px-4 py-2 rounded-tl-xl">Name</th>
                        <th class="px-4 py-2">Phone</th>
                        <th class="px-4 py-2">E-mail</th>
                        <th class="px-4 py-2 rounded-tr-xl">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(customer) in customers" :key="customer.id"
                        class="border-b border-blue-100 hover:bg-theme-3 transition text-center">
                        <td class="px-4 py-2">{{ customer.name }}</td>
                        <td class="px-4 py-2">
                            <button v-if="customer.phone" @click="copyToClipboard('Phone Number', customer.phone)"
                                class="p-1 text-xs bg-gray-200 rounded hover:bg-gray-300">
                                📋
                            </button>
                            {{ customer.phone }}
                        </td>
                        <td class="px-4 py-2">
                            <button v-if="customer.email" @click="copyToClipboard('Email', customer.email)"
                                class="p-1 text-xs bg-gray-200 rounded hover:bg-gray-300">
                                📋
                            </button>
                            {{ customer.email }}
                        </td>
                        <td class="px-4 py-2 flex justify-center gap-2">
                            <router-link :to="{ name: 'Orders', query: { customer: customer.id } }"
                                class="p-2 bg-green-100 hover:bg-green-200 rounded" title="History">
                                <Logs class="w-5 h-5 text-green-600" />
                            </router-link>
                            <button class="p-2 bg-blue-100 hover:bg-blue-200 rounded" title="Edit">
                                <LucideEdit class="w-5 h-5 text-blue-600" />
                            </button>
                            <button class="p-2 bg-red-100 hover:bg-red-200 rounded" title="Delete">
                                <LucideTrash class="w-5 h-5 text-red-600" />
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <PaginationComponent :currentPage="currentPage" :totalPages="totalPages" @page-change="handlePageChange" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { copyToClipboard } from "@/helpers/clipBoard.js";
import {
    LucideUserPlus,
    LucideUsers,
    LucideCheckCircle,
    Logs,
    LucideEdit,
    LucideTrash
} from "lucide-vue-next";
import PaginationComponent from '@/components/PaginationComponent.vue'
import PageTitleComponent from "@/components/PageTitleComponent.vue";

export default {
    name: "CustomerList",
    components: {
        LucideUserPlus,
        LucideUsers,
        LucideCheckCircle,
        Logs,
        LucideEdit,
        LucideTrash,
        PaginationComponent,
        PageTitleComponent,
    },
    props: {
        isModal: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            searchQuery: "",
            currentPage: this.$route.query.page || 1,
            pageLength: 10,
            customer_group: "",
        }
    },
    computed: {
        ...mapGetters([
            'customers', 'currentCustomerPageSize', 'totalCustomerCount',
        ]),
        totalPages() {
            return Math.ceil(this.totalCustomerCount / this.currentCustomerPageSize);
        },
    },
    watch: {
        '$route.query.page': {
            immediate: true,
            handler(newPage) {
                let page = parseInt(newPage, 10)

                if (!page || isNaN(page)) {
                    this.$router.replace({ query: { page: 1 } })
                    page = 1
                    return
                }

                this.currentPage = page
                this.fetchCustomers({ page, pageSize: this.currentCustomerPageSize })
            }
        }
    },
    methods: {
        ...mapActions(['fetchCustomers']),
        copyToClipboard,
        handlePageChange(page) {
            this.currentPage = page
            this.$router.push({ query: { page: this.currentPage } })
        },
    }
};
</script>


<style scoped></style>
